import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  MapPin,
  Phone,
  Send,
  Loader2,
  Clock,
  Paperclip,
  FileText,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

const initial = {
  name: "",
  organisation: "",
  phone: "",
  email: "",
  message: "",
};

const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const Contact = () => {
  const [values, setValues] = useState(initial);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const onChange = (e) =>
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    const isPdf =
      f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Please select a PDF file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("PDF is too large. Max 10 MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!values.name || !values.phone || !values.email || !values.message) {
      toast.error("Please fill in your name, phone, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", values.name.trim());
      if (values.organisation?.trim())
        fd.append("organisation", values.organisation.trim());
      fd.append("phone", values.phone.trim());
      fd.append("email", values.email.trim());
      fd.append("message", values.message.trim());
      fd.append("kind", "contact");
      if (file) fd.append("attachment", file, file.name);

      await axios.post(`${API}/contact`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Thanks, we'll be in touch within one business day.");
      setValues(initial);
      clearFile();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please call 0450 270 547.";
      toast.error(
        typeof msg === "string" ? msg : "Unable to send. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 sm:py-28 bg-[var(--ew-bg-2)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Contact</p>
            <div
              data-testid="response-time-badge"
              className="inline-flex items-center gap-2 mt-3 rounded-full border border-[var(--ew-sage)]/30 bg-[var(--ew-sage)]/10 px-3.5 py-1.5"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--ew-sage)] opacity-70 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--ew-accent)]" />
              </span>
              <Clock className="h-3.5 w-3.5 text-[var(--ew-accent)]" strokeWidth={2} />
              <span className="text-xs font-semibold text-[var(--ew-accent)] tracking-tight">
                Replies within 1 hour during business hours
              </span>
            </div>
            <h2 className="font-heading font-medium mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-[var(--ew-text)]">
              Let&apos;s talk about how we can help.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[var(--ew-text-2)] leading-relaxed">
              Whether you&apos;re a participant, family member, coordinator,
              provider or organisation, we&apos;d be happy to hear from you.
              Please share a few details and we&apos;ll follow up within one
              business day.
            </p>

            <div className="mt-9 space-y-4">
              <a
                href="tel:0450270547"
                data-testid="contact-phone"
                className="flex items-center gap-4 rounded-2xl border border-[#6E9C88]/15 bg-white p-5 hover:border-[var(--ew-sage)]/60 transition-colors"
              >
                <span className="icon-badge">
                  <Phone className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[var(--ew-text-muted)] font-semibold">
                    Phone
                  </p>
                  <p className="font-heading text-lg font-semibold text-[var(--ew-text)]">
                    0450 270 547
                  </p>
                </div>
              </a>

              <a
                href="mailto:everwellcommunitynursing@gmail.com"
                data-testid="contact-email"
                className="flex items-center gap-4 rounded-2xl border border-[#6E9C88]/15 bg-white p-5 hover:border-[var(--ew-sage)]/60 transition-colors"
              >
                <span className="icon-badge">
                  <Mail className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[var(--ew-text-muted)] font-semibold">
                    Email
                  </p>
                  <p className="font-heading text-base sm:text-lg font-semibold text-[var(--ew-text)] break-all">
                    everwellcommunitynursing@gmail.com
                  </p>
                </div>
              </a>

              <div
                data-testid="contact-location"
                className="flex items-center gap-4 rounded-2xl border border-[#6E9C88]/15 bg-white p-5"
              >
                <span className="icon-badge">
                  <MapPin className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[var(--ew-text-muted)] font-semibold">
                    Location
                  </p>
                  <p className="font-heading text-lg font-semibold text-[var(--ew-text)]">
                    Canberra, ACT
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="ew-card p-7 sm:p-10"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-[var(--ew-text)] mb-2"
                  >
                    Name <span className="text-[var(--ew-accent)]">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    data-testid="contact-name"
                    value={values.name}
                    onChange={onChange}
                    className="ew-input"
                    placeholder="Jane Smith"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="organisation"
                    className="block text-sm font-medium text-[var(--ew-text)] mb-2"
                  >
                    Organisation{" "}
                    <span className="text-[var(--ew-text-muted)] font-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    id="organisation"
                    name="organisation"
                    data-testid="contact-organisation"
                    value={values.organisation}
                    onChange={onChange}
                    className="ew-input"
                    placeholder="e.g. Support coordination team"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-[var(--ew-text)] mb-2"
                  >
                    Phone <span className="text-[var(--ew-accent)]">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    value={values.phone}
                    onChange={onChange}
                    className="ew-input"
                    placeholder="04xx xxx xxx"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[var(--ew-text)] mb-2"
                  >
                    Email <span className="text-[var(--ew-accent)]">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    data-testid="contact-email-input"
                    type="email"
                    value={values.email}
                    onChange={onChange}
                    className="ew-input"
                    placeholder="name@example.com"
                    required
                  />
                </div>
              </div>

              <div className="mt-5">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-[var(--ew-text)] mb-2"
                >
                  Participant needs / message{" "}
                  <span className="text-[var(--ew-accent)]">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="contact-message"
                  value={values.message}
                  onChange={onChange}
                  className="ew-input"
                  placeholder="Tell us briefly about the participant's needs, supports in place, and what you're hoping we can help with."
                  rows={6}
                  required
                />
              </div>

              <div className="mt-5">
                <label
                  htmlFor="attachment"
                  className="block text-sm font-medium text-[var(--ew-text)] mb-2"
                >
                  Attach referral PDF{" "}
                  <span className="text-[var(--ew-text-muted)] font-normal">
                    (optional, max 10 MB)
                  </span>
                </label>
                <input
                  ref={fileInputRef}
                  id="attachment"
                  name="attachment"
                  type="file"
                  accept="application/pdf,.pdf"
                  data-testid="contact-attachment"
                  onChange={onFileChange}
                  className="sr-only"
                />
                {!file ? (
                  <label
                    htmlFor="attachment"
                    data-testid="contact-attachment-trigger"
                    className="flex items-center gap-3 cursor-pointer rounded-2xl border border-dashed border-[#6E9C88]/40 bg-[var(--ew-bg-3)]/40 px-5 py-4 hover:border-[var(--ew-sage)] hover:bg-[var(--ew-bg-3)] transition-colors"
                  >
                    <span className="icon-badge">
                      <Paperclip className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <span className="text-sm text-[var(--ew-text-2)]">
                      <span className="font-semibold text-[var(--ew-accent)]">
                        Click to upload
                      </span>{" "}
                      a referral PDF (optional)
                    </span>
                  </label>
                ) : (
                  <div
                    data-testid="contact-attachment-preview"
                    className="flex items-center gap-3 rounded-2xl border border-[var(--ew-sage)]/40 bg-white px-5 py-4"
                  >
                    <span className="icon-badge">
                      <FileText className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--ew-text)] truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-[var(--ew-text-muted)]">
                        {formatBytes(file.size)} · PDF
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="contact-attachment-remove"
                      onClick={clearFile}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--ew-text-muted)] hover:bg-[var(--ew-bg-3)] hover:text-[var(--ew-accent)] transition-colors"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="btn-primary w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Message <Send className="h-4 w-4" strokeWidth={2} />
                    </>
                  )}
                </button>
                <p className="text-xs text-[var(--ew-text-muted)] leading-relaxed">
                  We&apos;ll respond within one business day. For urgent
                  clinical matters, please call 000.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
